import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Transforms, Editor as SlateEditor } from 'slate';
import { useSlate } from 'slate-react';
import { FileZone } from 'app/components';
import { DataImage } from 'app/components';
import { useImagesInDocument, useCreateImage } from 'entities/document';
import { defaultParagraph } from 'slate/extensions/paragraph';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { usePermissions } from 'app/api/permission-queries';
import { ImageElement } from 'slate/types/slate';

interface ImageFile {
  superId: string;
  revisionId: string;
  filename?: string;
  fileType: string;
  fileContent: string;
  [key: string]: unknown;
}

interface ImageItem {
  file: ImageFile;
  [key: string]: unknown;
}

interface ImportImageProps {
  onClose: (...args: unknown[]) => void;
  search?: string;
}

export const ImportImage = ({ onClose, search = '' }: ImportImageProps) => {
  const { t } = useTranslation('imageimport');

  const editor = useSlate();
  const { data: pDocument } = useEditorDocument();
  const { documentPermissions } = usePermissions(pDocument.superId);
  const canUpload = documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER');
  const {
    data: images,
    isLoading,
    refetch,
  } = useImagesInDocument(pDocument.superId) as { data: ImageItem[] | undefined; isLoading: boolean; refetch: () => void };
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { mutateAsync: createImage } = useCreateImage();

  const handleSelect = () => {
    if (!selectedImage) {
      return;
    }
    const slateImage: ImageElement = {
      type: 'image',
      uuid: uuidv4(),
      imageSuperId: selectedImage.file.superId,
      children: [],
    };
    if (editor.selection) {
      const point = SlateEditor.end(editor, editor.selection);
      Transforms.insertNodes(editor, [slateImage, defaultParagraph()], {
        at: point,
      });
    }
    setSelectedImage(null);
    onClose();
  };

  const handleUploadSuccess = async ({ data }: { data: { revisionId: string; superId: string } }) => {
    await createImage({
      documentSuperId: pDocument.superId,
      revisionId: data.revisionId,
      superId: data.superId,
    });
    if (isMounted.current) {
      refetch();
      setUploadingFile(null);
    }
  };
  const query = search.trim().toLowerCase();
  const visibleImages = query ? images?.filter((image) => (image.file?.filename ?? '').toLowerCase().includes(query)) : images;

  useEffect(() => {
    if (selectedImage && !visibleImages?.some((image) => image.file.revisionId === selectedImage.file.revisionId)) {
      setSelectedImage(null);
    }
  }, [selectedImage, visibleImages]);

  return (
    <div className="import-picker">
      {canUpload && (
        <FileZone
          type="img"
          old={null}
          documentId={pDocument.superId}
          onProgress={(progress) => setProgress(progress)}
          onFileSelect={(file) => setUploadingFile(file)}
          onSuccess={handleUploadSuccess}
        />
      )}
      <div className="progress-bar-wrapper">
        {uploadingFile && (
          <>
            <div className="progress-bar-label">
              <span>{uploadingFile?.name}</span>
              <span>{progress}%</span>
            </div>

            <div className="progress" style={{ width: '100%' }}>
              <div className="progress-bar" aria-valuenow={25} aria-valuemin={0} aria-valuemax={100} role="progressbar" style={{ width: `${progress}%` }}></div>
            </div>
          </>
        )}
      </div>
      {isLoading ? (
        <h2 style={{ marginTop: '20px', textAlign: 'center' }}>{t('loading')}</h2>
      ) : (
        <>
          {!visibleImages?.length ? (
            <h3 className="text-center import-picker-empty">{images?.length ? t('no-matches') : t('no-images')}</h3>
          ) : (
            <div className="image-grid">
              {visibleImages.map((image) => (
                <div
                  className={`import-image-cell ${selectedImage?.file?.revisionId === image.file.revisionId && 'selected-image'}`}
                  key={image.file.superId}
                  onClick={() => setSelectedImage(image)}
                >
                  <DataImage {...image.file} className="import-image-thumb" />
                  <span className="image-name" title={image.file?.filename}>
                    {image.file?.filename}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="image-footer">
            <button
              className="btn btn-secondary"
              onClick={(e) => {
                e.persist();
                onClose();
              }}
            >
              {t('close')}
            </button>
            <button className="btn btn-primary" disabled={!selectedImage} onClick={() => handleSelect()}>
              {t('select')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
